import { Box, Button, Center, Flex, Heading, Radio, RadioGroup, Textarea } from "@chakra-ui/react";
import React from "react";
import { Messages } from "../../../core/api";
import {
  CaregiverId,
  CommCenterLabelId,
  PatientId,
  VisitBroadcastId,
} from "../../../shared/schema/schema";
import { NewTicketRequestBody } from "../utils/communication-utils";
import ChooseLabelSection from "./ChooseLabelSection";
import { z } from "zod";
import { useAutosizeTextarea } from "../../../shared/hooks/useAutoresize";
import _ from "lodash";
import EntityFormControl from "../../workflow/components/EntityFormControl";

interface NewTicketError {
  labelId?: string[];
  message?: string[];
  topic?: string[];
  caregiverId?: string[];
  patientId?: string[];
}

interface NewTicketDetails {
  labelId: CommCenterLabelId | null;
  caregiverId: CaregiverId[] | null;
  patientId: PatientId[] | null;
  message: string;
  topic: "Patient" | "Caregiver";
  visitBroadcastId?: VisitBroadcastId;
}

interface Props {
  initialLabelId: CommCenterLabelId | null;
  labels: Messages["CommCenterLabel"][];
  defaultValues: {
    caregiverId: CaregiverId | null;
    patientId: PatientId | null;
    topic: "Caregiver" | "Patient";
    message?: string;
    label?: Messages["CommCenterLabel"];
    visitBroadcastId?: VisitBroadcastId;
  };
  onCreateTicket: (details: NewTicketRequestBody) => void;
}

const newDetailsSchema = z
  .object({
    labelId: z.number({ required_error: "Label is missing " }).transform(CommCenterLabelId.parse),
    message: z.string().min(1, { message: "Message is missing" }),
    visitBroadcastId: z.number().transform(VisitBroadcastId.parse).optional(),
  })
  .and(
    z.union([
      z.object({
        topic: z.literal("Caregiver"),
        caregiverId: z.array(
          z.number({ required_error: "Caregiver is missing" }).transform(CaregiverId.parse)
        ),
        patientId: z.array(z.number().transform(PatientId.parse)).nullable(),
      }),
      z.object({
        topic: z.literal("Patient"),
        patientId: z.array(
          z.number({ required_error: "Patient is missing" }).transform(PatientId.parse)
        ),
        caregiverId: z.array(z.number().transform(CaregiverId.parse)).nullable(),
      }),
    ])
  );

const NewTicket = (props: Props) => {
  const [chosenLabels, setChosenLabels] = React.useState<Messages["CommCenterLabel"][]>(() => {
    const retLabels: Messages["CommCenterLabel"][] = [];
    if (!props.defaultValues.label) return retLabels;

    let parentLabel: Messages["CommCenterLabel"] | undefined = props.labels.find(
      (label) => label.id === props.defaultValues.label?.parent
    );
    while (parentLabel) {
      retLabels.push(parentLabel);
      parentLabel = props.labels.find((label) => label.id === parentLabel?.parent);
    }
    return retLabels.reverse();
  });

  const [labelsToDisplay, setLabelsToDisplay] = React.useState<Messages["CommCenterLabel"][]>(
    () => {
      if (!props.defaultValues.label)
        return props.labels.filter((label) => label.parent === props.initialLabelId);
      return props.labels.filter((label) => label.parent === props.defaultValues.label?.parent);
    }
  );

  const ref = React.useRef<HTMLTextAreaElement>(null);
  useAutosizeTextarea(ref, { lineHeight: 64 });

  const [newTicket, setNewTicket] = React.useState<NewTicketDetails>({
    caregiverId: props.defaultValues.caregiverId ? [props.defaultValues.caregiverId] : null,
    patientId: props.defaultValues.patientId ? [props.defaultValues.patientId] : null,
    topic: props.defaultValues.topic,
    labelId: props.defaultValues.label?.id ?? null,
    message: props.defaultValues.message ?? "",
    visitBroadcastId: props.defaultValues.visitBroadcastId,
  });

  const [errors, setErrors] = React.useState<NewTicketError | null>(() => {
    const retErrors: NewTicketError = {};
    if (newTicket.labelId === null) retErrors.labelId = ["Label id missing"];
    if (newTicket.message === null) retErrors.message = ["Message is missing"];
    return retErrors;
  });

  const handleChangeTicketDetails = (field: Partial<NewTicketDetails>) => {
    const newDetails = {
      ...newTicket,
      ...field,
    };

    setNewTicket(newDetails);

    const result = newDetailsSchema.safeParse(newDetails);
    setErrors(!result.success ? result.error.flatten().fieldErrors : null);
  };

  const onClickCreateTicket = () => {
    const result = newDetailsSchema.safeParse(newTicket);

    if (!result.success) {
      setErrors(result.error.flatten().fieldErrors);
      return;
    }

    props.onCreateTicket(result.data);
  };

  const handleClickLabel = (labelClicked: Messages["CommCenterLabel"]) => {
    const labelsToDisplay = props.labels.filter((label) => label.parent === labelClicked.id);

    if (labelsToDisplay.length !== 0) {
      setChosenLabels([...chosenLabels, labelClicked]);
      setLabelsToDisplay(labelsToDisplay);
    } else {
      handleChangeTicketDetails({ labelId: labelsToDisplay.length === 0 ? labelClicked.id : null });
    }
  };

  const handleClickChosenLabel = (chosenLabel: Messages["CommCenterLabel"]) => {
    const labelIndex = chosenLabels.indexOf(chosenLabel);
    setChosenLabels(chosenLabels.slice(0, labelIndex));

    const labelsToDisplay = props.labels.filter((label) => label.parent === chosenLabel.parent);
    setLabelsToDisplay(labelsToDisplay);

    handleChangeTicketDetails({ labelId: labelsToDisplay.length === 0 ? chosenLabel.id : null });
  };

  return (
    <Flex align="center" bg="white" direction="column" gap={10} w="full">
      <Heading color="blue.500">Let&rsquo;s start a new conversation</Heading>
      <Flex>
        <RadioGroup
          value={newTicket.topic}
          onChange={(value: "Caregiver" | "Patient") => handleChangeTicketDetails({ topic: value })}
        >
          <Flex alignItems="flex-start" direction="column" gap={2}>
            <Flex gap={4}>
              <Radio checked={newTicket.topic === "Caregiver"} value="Caregiver">
                Primary
              </Radio>
              <EntityFormControl
                errors={[]}
                input={{ type: "entity", entity: "Caregiver" }}
                isRequired={false}
                label="Caregivers"
                multiple={true}
                showLabel={false}
                value={newTicket.caregiverId ?? null}
                onChange={(value) => handleChangeTicketDetails({ caregiverId: value ?? null })}
              />
            </Flex>
            <Flex gap={4}>
              <Radio checked={newTicket.topic === "Patient"} value="Patient">
                Primary
              </Radio>
              <EntityFormControl
                errors={[]}
                input={{ type: "entity", entity: "Patient" }}
                isRequired={false}
                label="Patients"
                multiple={true}
                showLabel={false}
                value={newTicket.patientId ?? null}
                onChange={(value) => handleChangeTicketDetails({ patientId: value ?? null })}
              />
            </Flex>
          </Flex>
        </RadioGroup>
      </Flex>
      <Center>
        <ChooseLabelSection
          chosenLabels={chosenLabels}
          labels={labelsToDisplay}
          value={props.labels.find((label) => label.id === newTicket.labelId)}
          onChange={handleClickLabel}
          onClickChosenLabel={handleClickChosenLabel}
        />
      </Center>
      <Box maxW="xl" w="full">
        <Textarea
          ref={ref}
          borderRadius="2xl"
          lineHeight="1.6"
          minHeight="64px"
          placeholder="Type your message..."
          resize="none"
          rows={2}
          size="lg"
          value={newTicket.message}
          onChange={(e) => handleChangeTicketDetails({ message: e.target.value })}
        />
      </Box>
      <Button colorScheme="blue" disabled={!_.isEmpty(errors)} onClick={onClickCreateTicket}>
        Send
      </Button>
    </Flex>
  );
};

export default NewTicket;
